import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Define User type locally
export type Brand<T, K> = T & { readonly __brand: K };

// Mode preference for post-login selection
export type ModePreference = 'hub' | 'lite' | 'ask';

export type UserId = Brand<string, 'UserId'>;
export type OrganizationId = Brand<string, 'OrganizationId'>;
export type RoleId = Brand<string, 'RoleId'>;

export interface User {
  id: UserId;
  email: string;
  firstName: string;
  lastName: string;
  role: RoleId;
  secondaryRoles: string[];
  permissions: string[];
  organizationId?: OrganizationId;
  organizationName?: string;
  authProvider: 'local' | 'oidc';
  mfaEnabled: boolean;
  forcePasswordChange: boolean;
  isActive: boolean;
  preferences: {
    notifications: {
      email: boolean;
      sms: boolean;
      discord: boolean;
    };
    timezone: string;
    dateFormat: string;
  };
  secondaryEmail?: string;
  // Inspector-specific fields
  phone?: string;
  certificationNumber?: string;
  signatureUrl?: string;
  homeAddress?: string;
  homeLatitude?: number;
  homeLongitude?: number;
  createdAt: string;
  updatedAt: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  preferredMode: ModePreference;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  updateTokens: (accessToken: string, refreshToken: string) => void;
  setPreferredMode: (mode: ModePreference) => void;
  setForcePasswordChange: (value: boolean) => void;
  updateUser: (partial: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      preferredMode: 'ask' as ModePreference,
      login: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        }),
      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        }),
      updateTokens: (accessToken, refreshToken) =>
        set({
          accessToken,
          refreshToken,
        }),
      setPreferredMode: (mode) =>
        set({
          preferredMode: mode,
        }),
      setForcePasswordChange: (value) =>
        set((state) => ({
          user: state.user
            ? { ...state.user, forcePasswordChange: value }
            : null,
        })),
      updateUser: (partial) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...partial } : null,
        })),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        preferredMode: state.preferredMode,
      }),
    }
  )
);
